import { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { Mail, Check, Calendar, MessageSquare, Image as ImageIcon, Clock, Users, Brain, Bell, Sparkles, ChevronRight, Menu, X, Shield, Zap, Home, Network, TrendingUp, CheckCircle, Lock, FileText, AlertCircle, Type } from 'lucide-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Admin from './Admin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function HomePage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userCountry, setUserCountry] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);

  // Detect user's country on component mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        setUserCountry(data.country_code);
      } catch (error) {
        console.error('Error detecting location:', error);
        setUserCountry('US'); // Default to US if detection fails
      } finally {
        setIsLoadingLocation(false);
      }
    };
    detectCountry();
  }, []);

  // Pricing based on country
  const pricing = userCountry === 'IN' ? {
    currency: '₹',
    starter: '199',
    household: '299',
    familyPlus: '699'
  } : {
    currency: '$',
    starter: '5',
    household: '10',
    familyPlus: '20'
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/waitlist`, {
        email,
        name,
      });

      setSubmitStatus({ type: 'success', message: 'Successfully joined the waitlist!' });
      setEmail('');
      setName('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      setSubmitStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-slate-200" role="navigation" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center space-x-3 no-underline" data-testid="header-logo-link">
              <div className="w-9 h-9 overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#0A3E4A', borderRadius: '16px' }}>
                <img 
                  src="/fampilot-icon.png" 
                  alt="fampilot icon" 
                  className="w-full h-full object-cover"
                  style={{ transform: 'scale(1.8)' }}
                />
              </div>
              <span className="text-xl" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                <span style={{ fontWeight: 300, color: '#1e293b' }}>fam</span>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>pilot</span>
              </span>
            </a>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition">Features</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition">How it works</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition">Pricing</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 transition">FAQ</a>
              <button 
                onClick={() => document.getElementById('cta-section').scrollIntoView({ behavior: 'smooth' })}
                className="px-6 py-2 bg-gradient-to-r from-teal-500 to-yellow-400 text-white rounded-lg hover:shadow-lg transition"
                data-testid="nav-cta-button"
              >
                Get Started
              </button>
            </div>

            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3" data-testid="mobile-menu">
              <a href="#features" className="block text-slate-600 hover:text-slate-900 transition">Features</a>
              <a href="#how-it-works" className="block text-slate-600 hover:text-slate-900 transition">How it works</a>
              <a href="#pricing" className="block text-slate-600 hover:text-slate-900 transition">Pricing</a>
              <a href="#faq" className="block text-slate-600 hover:text-slate-900 transition">FAQ</a>
              <button 
                onClick={() => {
                  document.getElementById('cta-section').scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
                className="w-full px-6 py-2 bg-gradient-to-r from-teal-500 to-yellow-400 text-white rounded-lg hover:shadow-lg transition"
              >
                Get Started
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <main>
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-tight" data-testid="hero-headline">
              Your household, <br />
              <span className="bg-gradient-to-r from-teal-500 to-yellow-400 bg-clip-text text-transparent">on autopilot.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-4 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 400 }} data-testid="hero-subheadline">
              From school emails to WhatsApp chaos — <span style={{ fontWeight: 300 }}>fam</span><span style={{ fontWeight: 700 }}>pilot</span> turns everything into <span className="font-bold text-slate-900">one simple daily plan</span> for your family.
              <br /><br />
              <span className="font-bold text-slate-900">Just forward it. The AI figures it out.</span>
            </p>
            
            {/* Trust Line */}
            <div className="inline-flex items-center space-x-2 bg-green-50 border border-green-200 px-6 py-3 rounded-full mb-8">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800 font-semibold">No forms. No setup. No integrations.</span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button 
                onClick={() => document.getElementById('cta-section').scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-gradient-to-r from-teal-500 to-yellow-400 text-white text-lg font-semibold rounded-xl hover:shadow-xl transition-all transform hover:scale-105"
                data-testid="hero-primary-cta"
              >
                Get your family on autopilot
              </button>
              <button 
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-white text-slate-700 text-lg font-semibold rounded-xl border-2 border-slate-200 hover:border-blue-600 hover:shadow-lg transition-all"
                data-testid="hero-secondary-cta"
              >
                See how it works
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: How fampilot works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="core-idea-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-16">
            How <span style={{ fontWeight: 300 }}>fam</span><span style={{ fontWeight: 700 }}>pilot</span> works
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Left: Inputs */}
            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">You send:</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-slate-900">School email</span>
                  </div>
                  <p className="text-sm text-slate-600">"Basketball practice Thursday 6 PM"</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <MessageSquare className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-slate-900">WhatsApp message</span>
                  </div>
                  <p className="text-sm text-slate-600">"Doctor appointment next week"</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <FileText className="w-5 h-5 text-teal-600" />
                    <span className="font-semibold text-slate-900">PDF circular</span>
                  </div>
                  <p className="text-sm text-slate-600">School event schedule</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <Type className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-slate-900">Just type it</span>
                  </div>
                  <p className="text-sm text-slate-600">"Piano lesson tomorrow 4pm"</p>
                </div>
              </div>
            </div>

            {/* Middle: AI Processing */}
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 flex flex-col justify-center items-center">
              <Brain className="w-16 h-16 text-teal-600 mb-6" />
              <h3 className="text-xl font-bold text-slate-900 mb-4">AI understands</h3>
              <div className="space-y-2 text-center">
                <p className="text-sm text-slate-700 flex items-center justify-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>Extracts events</span>
                </p>
                <p className="text-sm text-slate-700 flex items-center justify-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>Assigns tasks</span>
                </p>
                <p className="text-sm text-slate-700 flex items-center justify-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span>Coordinates family</span>
                </p>
              </div>
            </div>

            {/* Right: Outputs */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">You get:</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <Calendar className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-slate-900">Calendar updated</span>
                  </div>
                  <p className="text-xs text-slate-600">Thursday, 6:00 PM - Basketball</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-slate-900">Tasks created</span>
                  </div>
                  <p className="text-xs text-slate-600">Book doctor appointment</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <Users className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-slate-900">Family coordinated</span>
                  </div>
                  <p className="text-xs text-slate-600">Mom handles drop-off</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xl text-slate-700 mb-2">
              Stop manually entering events, copying messages, and coordinating schedules.
            </p>
            <p className="text-2xl font-bold bg-gradient-to-r from-teal-500 to-yellow-400 bg-clip-text text-transparent">
              Just send things to <span style={{ fontWeight: 300 }}>fam</span><span style={{ fontWeight: 700 }}>pilot</span> — the AI organizes everything.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: Wow Moment */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-teal-50" data-testid="wow-moment-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-16">
            Not just organized. <br />Actually <span className="text-blue-600">planned</span>.
          </h2>

          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-slate-900">Today's Plan</h3>
                <span className="text-sm text-slate-600">Thursday, March 16</span>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-lg">
                  <Clock className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-semibold text-slate-900">Leave home at 5:35 PM</p>
                    <p className="text-sm text-slate-600">15 min drive to practice</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-teal-50 rounded-lg">
                  <Users className="w-6 h-6 text-teal-600" />
                  <div>
                    <p className="font-semibold text-slate-900">Mom handles basketball drop</p>
                    <p className="text-sm text-slate-600">Dad available at 6:15 PM</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg">
                  <Calendar className="w-6 h-6 text-slate-600" />
                  <div>
                    <p className="font-semibold text-slate-900">Dad joins meeting till 6 PM</p>
                    <p className="text-sm text-slate-600">Can pick up after</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <AlertCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">⚠️ Conflict avoided</p>
                  <p className="text-sm text-green-700">AI detected overlap and adjusted plan</p>
                </div>
              </div>
            </div>

            <p className="text-xl text-center text-slate-700">
              <span style={{ fontWeight: 300 }}>fam</span><span style={{ fontWeight: 700 }}>pilot</span> doesn't just store events —<br />
              <span className="font-bold text-slate-900">it understands your family's schedule and helps you act on it.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: Built for Real Families */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="real-families-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-16">
            Built for how families <span className="text-teal-600">actually work</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-8">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-6">
                <Home className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">👨‍👩‍👧‍👦 Your household, fully managed</h3>
              <p className="text-slate-600 text-lg">
                Parents, kids, schedules, tasks, notes, reminders — all in one place -- powered by AI. No juggling multiple apps.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-pink-50 rounded-2xl p-8">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                <Network className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">🔗 Coordinate with extended family when needed</h3>
              <p className="text-slate-600 text-lg">
                Share plans with grandparents, siblings, cousins, nannies, or other caregivers. They see what they need.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8">
              <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">📅 Works for everyday life and big events</h3>
              <p className="text-slate-600 text-lg">
                School schedules, birthday parties, vacations — <span style={{ fontWeight: 300 }}>fam</span><span style={{ fontWeight: 700 }}>pilot</span> handles it all.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">🧠 Learns your routines over time</h3>
              <p className="text-slate-600 text-lg">
                The more you use it, the smarter it gets. Suggestions improve. Plans get better.
              </p>
            </div>
          </div>

          <div className="text-center bg-gradient-to-r from-teal-500 to-yellow-400 text-white rounded-2xl p-8">
            <p className="text-2xl font-semibold mb-2">Works for your household today.</p>
            <p className="text-2xl font-semibold">Scales to your entire family network tomorrow.</p>
          </div>
        </div>
      </section>


      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-6">
            Everything your family needs <br />to stay organized
          </h2>
          <p className="text-xl text-center text-slate-600 mb-16 max-w-2xl mx-auto">
            <span style={{ fontWeight: 300 }}>fam</span><span style={{ fontWeight: 700 }}>pilot</span> AI handles all the logistics so you can focus on what matters.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition" data-testid="feature-ai-detection">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-6">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">AI Event Detection</h3>
              <p className="text-slate-600 text-lg mb-6">
                Automatically detects events from emails, photos, PDFs, and shared messages.
              </p>
              <img 
                src="https://images.unsplash.com/photo-1640622302099-f10b18de7b16?w=600&q=80" 
                alt="Family using technology together on a laptop - FamPilot AI event detection feature"
                className="rounded-xl w-full h-48 object-cover"
                loading="lazy"
                width="600"
                height="192"
              />
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-slate-50 to-teal-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition" data-testid="feature-coordination">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Family Schedule Coordination</h3>
              <p className="text-slate-600 text-lg mb-6">
                The AI identifies conflicts and proposes the best plan.
              </p>
              <img 
                src="https://images.unsplash.com/photo-1762343282367-555dbd018314?w=600&q=80" 
                alt="Happy family coordinating schedules together - FamPilot family schedule coordination"
                className="rounded-xl w-full h-48 object-cover"
                loading="lazy"
                width="600"
                height="192"
              />
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-slate-50 to-green-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition" data-testid="feature-reminders">
              <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                <Bell className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Smart Reminders</h3>
              <p className="text-slate-600 text-lg mb-4">
                Everyone gets the right reminders at the right time.
              </p>
              <p className="text-slate-500 mb-6">
                No more forgotten practices or missed appointments.
              </p>
              <img 
                src="https://images.unsplash.com/photo-1564156280315-1d42b4651629?w=600&q=80" 
                alt="Father smiling with child - Smart family reminders that keep everyone on track"
                className="rounded-xl w-full h-48 object-cover"
                loading="lazy"
                width="600"
                height="192"
              />
            </div>

            {/* Feature 4 */}
            <div className="bg-gradient-to-br from-slate-50 to-orange-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition" data-testid="feature-memory">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Household Memory</h3>
              <p className="text-slate-600 text-lg mb-4">
                The AI learns your family's routines, activities, and preferences.
              </p>
              <p className="text-slate-500 mb-6">
                Over time it becomes your family operating system.
              </p>
              <img 
                src="https://images.unsplash.com/photo-1713942590392-598b193afb1a?w=600&q=80" 
                alt="Family spending quality time together - FamPilot household memory learns your routines"
                className="rounded-xl w-full h-48 object-cover"
                loading="lazy"
                width="600"
                height="192"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Continue with Section 5... */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="privacy-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-8">
            You stay in <span className="text-blue-600">control</span>
          </h2>
          <p className="text-xl text-center text-slate-600 mb-16 max-w-2xl mx-auto">
            Built for privacy-first families.
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
            <div className="flex items-start space-x-4 bg-slate-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">No Gmail access required</h3>
                <p className="text-slate-600">Forward emails on your terms. We never access your inbox.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 bg-slate-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">No calendar syncing needed</h3>
                <p className="text-slate-600"><span style={{ fontWeight: 300 }}>fam</span><span style={{ fontWeight: 700 }}>pilot</span> is your calendar. No permissions required.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 bg-slate-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">You choose what to share</h3>
                <p className="text-slate-600">Share only what matters. Nothing more, nothing less.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 bg-slate-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Brain className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">AI only processes what you send</h3>
                <p className="text-slate-600">No background scanning. No hidden access. Complete transparency.</p>
              </div>
            </div>
          </div>

          <div className="text-center bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 border border-green-200">
            <Lock className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <p className="text-2xl font-bold text-slate-900">
              Built for privacy-first families.
            </p>
          </div>
        </div>
      </section>



      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-16">
            What families are saying
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "It feels like we hired a family operations manager.",
                author: "Sarah M.",
                role: "Mom of 3",
                img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80"
              },
              {
                quote: "This removed the mental load of managing our kids' schedules.",
                author: "David K.",
                role: "Working parent",
                img: "https://images.unsplash.com/photo-1713942590392-598b193afb1a?w=400&q=80"
              },
              {
                quote: "I can't imagine running our household without this now.",
                author: "Jennifer L.",
                role: "Busy mom",
                img: "https://images.unsplash.com/photo-1758612897035-f75eb62f9f57?w=400&q=80"
              }
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition" data-testid={`testimonial-${idx + 1}`}>
                <div className="mb-6">
                  <img 
                    src={testimonial.img} 
                    alt={`${testimonial.author}, ${testimonial.role} - FamPilot testimonial`}
                    className="w-16 h-16 rounded-full object-cover mb-4"
                    loading="lazy"
                    width="64"
                    height="64"
                  />
                  <p className="text-lg text-slate-700 italic mb-4">"{testimonial.quote}"</p>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-slate-600 text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 8: Pricing */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-teal-50" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-6">
            Built for families who want life to run smoothly
          </h2>
          <p className="text-xl text-center text-slate-600 mb-4">
            Try <span style={{ fontWeight: 300 }}>fam</span><span style={{ fontWeight: 700 }}>pilot</span> free for 30 days. No credit card required.
          </p>
          
          <div className="flex items-center justify-center space-x-2 mb-16">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            <p className="text-lg font-semibold text-slate-700">All plans start with a 30-day free trial. No commitment.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Starter Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition border border-slate-200" data-testid="pricing-starter">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">🟢 Starter</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-4xl font-bold text-slate-900">{pricing.currency}{pricing.starter}</span>
                  <span className="text-slate-600 ml-2">/month per household</span>
                </div>
                <p className="text-slate-600 font-medium mb-4">Best for: <span className="text-slate-900">small families getting started</span></p>
              </div>
              
              <div className="mb-8">
                <p className="font-semibold text-slate-700 mb-3">Includes:</p>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Core timeline (Today view)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Basic events & tasks</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Limited AI extraction (10-15 items/month)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Household members</span>
                  </li>
                </ul>
              </div>
              
              <button 
                onClick={() => document.getElementById('cta-section').scrollIntoView({ behavior: 'smooth' })}
                className="w-full py-3 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300 transition"
                data-testid="pricing-starter-cta"
              >
                Start free trial
              </button>
            </div>

            {/* Household Plan - Most Popular */}
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 shadow-2xl hover:shadow-3xl transition transform scale-105 relative" data-testid="pricing-household">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-yellow-400 text-slate-900 text-sm font-bold py-2 px-6 rounded-full shadow-lg">
                  ⭐️ MOST POPULAR
                </span>
              </div>
              
              <div className="mb-6 mt-4">
                <h3 className="text-2xl font-bold text-white mb-2">🔵 Household</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-4xl font-bold text-white">{pricing.currency}{pricing.household}</span>
                  <span className="text-blue-100 ml-2">/month per household</span>
                </div>
                <p className="text-blue-100 font-medium mb-4">Best for: <span className="text-white">families managing daily life</span></p>
              </div>
              
              <div className="mb-8">
                <p className="font-semibold text-white mb-3">Includes:</p>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                    <span className="text-white">Unlimited events, tasks, notes</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                    <span className="text-white">Full AI ingestion (email, PDF, image, text)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                    <span className="text-white">AI planning & suggestions</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                    <span className="text-white">Reminders & coordination</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-300 mt-0.5 flex-shrink-0" />
                    <span className="text-white">Create and manage family networks</span>
                  </li>
                </ul>
              </div>
              
              <button 
                onClick={() => document.getElementById('cta-section').scrollIntoView({ behavior: 'smooth' })}
                className="w-full py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition shadow-lg"
                data-testid="pricing-household-cta"
              >
                Start your family on autopilot
              </button>
            </div>

            {/* Family+ Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition border border-slate-200" data-testid="pricing-family-plus">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">🟣 Family+</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-4xl font-bold text-slate-900">{pricing.currency}{pricing.familyPlus}</span>
                  <span className="text-slate-600 ml-2">/month per household</span>
                </div>
                <p className="text-slate-600 font-medium mb-4">Best for: <span className="text-slate-900">complex or highly coordinated families</span></p>
              </div>
              
              <div className="mb-8">
                <p className="font-semibold text-slate-700 mb-3">Everything in Household, plus:</p>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Advanced AI planning</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Priority processing</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Smarter suggestions over time</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Enhanced coordination across multiple families</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">Early access to new features</span>
                  </li>
                </ul>
              </div>
              
              <button 
                onClick={() => document.getElementById('cta-section').scrollIntoView({ behavior: 'smooth' })}
                className="w-full py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition"
                data-testid="pricing-family-plus-cta"
              >
                Unlock advanced coordination
              </button>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-slate-600">
              🔥 All plans start with a 30-day free trial. No commitment.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section - GEO Optimized */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="faq-section" id="faq" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-slate-900 mb-16">
            Frequently asked questions
          </h2>

          <div className="space-y-4">
            {[
              {
                q: "What is FamPilot?",
                a: "FamPilot is an AI-powered family scheduling and household management app. It automatically organizes your family's schedules, events, and tasks by processing forwarded emails, shared messages, uploaded photos, PDFs, and natural language text input. Think of it as an operating system for modern families."
              },
              {
                q: "How does FamPilot work?",
                a: "Simply forward emails, share messages, upload photos or PDFs, or type naturally. FamPilot's AI automatically extracts events and tasks, assigns them to the right family members, detects scheduling conflicts, and creates a coordinated daily plan for your entire household. No forms, no setup, no integrations required."
              },
              {
                q: "Is FamPilot free to try?",
                a: "Yes! All FamPilot plans come with a 30-day free trial. No credit card is required to get started. Plans start at $5/month (Starter), $10/month (Household, most popular), and $20/month (Family+) per household."
              },
              {
                q: "Is my family's data private and secure?",
                a: "Absolutely. FamPilot is built privacy-first. We never access your Gmail inbox or sync your existing calendars. You choose what to share by forwarding or uploading only the information you want processed. The AI only processes what you explicitly send — no background scanning, no hidden access."
              },
              {
                q: "What types of input does FamPilot accept?",
                a: "FamPilot accepts four types of input: forwarded emails (school newsletters, activity schedules), shared messages (WhatsApp, SMS), uploaded documents (PDFs, photos of circulars), and natural language text — simply type something like 'Piano lesson tomorrow at 4pm'."
              },
              {
                q: "Does FamPilot replace my existing calendar?",
                a: "FamPilot is designed to be your family's primary calendar and coordination hub. Unlike traditional calendar apps, it doesn't require syncing permissions. It understands your family's full context, detects conflicts across all members, and proactively coordinates schedules."
              },
              {
                q: "Can extended family members use FamPilot?",
                a: "Yes! You can share plans with grandparents, siblings, cousins, nannies, or other caregivers. They see only the information relevant to them. The Family+ plan includes enhanced coordination across multiple family networks."
              }
            ].map((faq, idx) => (
              <details 
                key={idx} 
                className="group bg-slate-50 rounded-xl border border-slate-200 overflow-hidden"
                itemScope 
                itemProp="mainEntity" 
                itemType="https://schema.org/Question"
                data-testid={`faq-item-${idx + 1}`}
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none hover:bg-slate-100 transition">
                  <h3 className="text-lg font-semibold text-slate-900 pr-4" itemProp="name">{faq.q}</h3>
                  <ChevronRight className="w-5 h-5 text-slate-500 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-6 pb-6" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-slate-600 leading-relaxed" itemProp="text">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Section 9: Final CTA */}
      <section id="cta-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-500 to-teal-600 text-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Stop managing. Start living.
          </h2>

          <div className="bg-white rounded-2xl p-8 max-w-md mx-auto mt-12">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Get your family on autopilot</h3>
            {submitStatus && (
              <div className={`mb-4 p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid="submit-status-message">
                {submitStatus.message}
              </div>
            )}
            <form onSubmit={handleWaitlistSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
                data-testid="waitlist-name-input"
              />
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
                data-testid="waitlist-email-input"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-yellow-400 text-white rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
                data-testid="waitlist-submit-button"
              >
                {isSubmitting ? 'Joining...' : 'Join the Waitlist'}
              </button>
            </form>
            <p className="text-slate-500 text-sm mt-4">30-day free trial. No credit card required.</p>
          </div>
        </div>
      </section>

      </main>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white" data-testid="footer" role="contentinfo">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-11 h-11 overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#0A3E4A', borderRadius: '18px' }}>
              <img 
                src="/fampilot-icon.png" 
                alt="fampilot icon" 
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.8)' }}
              />
            </div>
            <span className="text-2xl" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              <span style={{ fontWeight: 300, color: '#C4D9E2' }}>fam</span>
              <span style={{ fontWeight: 700, color: '#F0F7FA' }}>pilot</span>
            </span>
          </div>
          <p className="text-slate-400 text-lg mb-8">
            The operating system for modern families — powered by AI.
          </p>
          <div className="border-t border-slate-800 pt-8">
            <p className="text-slate-500 text-sm">© 2025 <span style={{ fontWeight: 300 }}>fam</span><span style={{ fontWeight: 700 }}>pilot</span>. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;